import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { AboutContext } from '../../../context/aboutContext';
import { GeneralContext } from '../../../context/generalContext';
import classes from './AboutBeer.module.scss';
import aboutBeerTopImg from '../../../images/about-beer-top-img.webp';
import aboutBeerImg1 from '../../../images/about-beer-img-1.webp';
import aboutBeerImg2 from '../../../images/about-beer-img-2.webp';
import aboutBeerImg3 from '../../../images/about-beer-img-3.webp';
import aboutBeerImg4 from '../../../images/about-beer-img-4.webp';
import aboutBeerImg5 from '../../../images/about-beer-img-5.webp';

export default function AboutBeer() {
  const { setActiveSection } = useContext(AboutContext);
  const { path, activeLanguage } = useContext(GeneralContext);
  // при изменении path проверяем в каком разделе находимся в данный момент и устанавливаем активный раздел
  useEffect(() => {
    if (window.location.pathname.slice(-6) === 'lambic') {
      setActiveSection(3);
    }
  }, [path]);

  // ID для youtube видео
  const [youtubeID] = useState('286HQEhynzk');

  return (
    <div className={classes.wrap}>
      <Helmet>
        <title>
          {activeLanguage === 1 ? 'Знакомьтесь, пиво Ламбик | Brasserie Lambic' : 'Meet Lambic beer | Brasserie Lambic'}
        </title>
      </Helmet>
      <div className={classes.topImg}>
        <LazyLoadImage src={aboutBeerTopImg} alt='' />
      </div>

      <section className={classes.section}>
        <div className={classes.title}>
          {activeLanguage === 1 ? 'Происхождение и производство' : 'Origination and production'}
        </div>
        <p>
          {activeLanguage === 1
            ? 'Принято считать, что название пива Ламбик произошло от известного своими традициями пивоварения старинного бельгийского города Lembeek.'
            : 'It is generally believed that the name of Lambic beer originated from old Belgian town Lembeek famous for its brewing traditions.'}
        </p>
        <p>
          {activeLanguage === 1
            ? 'В нашей брассерии насчитывается более 250 популярных и редких сортов этого уникального напитка. И слово «уникальный» — это вовсе не фигура речи. Отличительная особенность состоит в том, что при его производстве не применяются пивные дрожжи, это пиво самопроизвольной ферментации. Брожение происходит в дубовых бочках из-под вина благодаря местным диким дрожжам — микроорганизмам, обитающим в воздухе и затем прижившимся на стенках самих бочек. Вот почему географический ареал производителей пива Ламбик сужен до единственной провинции близ Брюсселя.'
            : 'There are more than 250 popular and rare types of this unique drink in our Brasserie. The word «unique» is not a figure of speech at all. The distinctive feature is that beer yeast is not used in its production, this is the beer of self-existing fermentation. Fermentation happens in oak barrels from wine thanks to local wild yeast — microorganisms existing in the air and then struck root into the walls of the barrels. That is why the geographic range of Lambic beer producers is narrowed to a single province near Brussel.'}
        </p>
        <p>
          {activeLanguage === 1
            ? 'При изготовлении помимо обычных составляющих — ячменного солода и непророщенных зерен пшеницы — используется сушеный хмель. Предварительно его выдерживают не менее трех лет, чтобы он уменьшил свою горечь и аромат. Производство ограничено еще и календарно, так как брожение сусла на открытом воздухе можно проводить только в прохладное время — с октября по май, так как летом появляется много посторонних бактерий, которые могут испортить вкус пива и вывести из-под контроля процесс брожения.'
            : 'Dried hops is used for making beer in addition to regular components — malted barley and wheat grains. At first it is aged for at least three years to reduce its bitterness and aroma. The production is limited by the seasons, because the wort can ferment in open air only during cool time — from October to May, since many foreign bacteria appear in summer, which can ruin the beer taste and take the fermentation process out of control.'}
        </p>
        <iframe
          className={classes.ytVideo}
          title='Youtube player'
          allowFullScreen
          frameBorder='0'
          sandbox='allow-same-origin allow-forms allow-popups allow-scripts allow-presentation'
          src={`https://youtube.com/embed/${youtubeID}?autoplay=0`}
        />
      </section>

      <section className={classes.section}>
        <div className={classes.title}>{activeLanguage === 1 ? 'Купажирование' : 'Blending'}</div>
        <p>
          {activeLanguage === 1
            ? 'Еще одной отличительной особенностью пива Ламбик является смешивание пива разных лет выдержки для получения нового сорта. Иногда Ламбик может состоять из пива пяти-шести разных лет. В некоторые сорта добавляют фрукты и травы. Ламбик отличается сложной композицией, но при этом пиво всегда узнаваемо по присущей только ему «фирменной» легкой кислинке. О пиве Ламбик трудно рассказать, его надо обязательно пробовать.'
            : 'One more special feature of Lambic beer is mixing beer of different aging periods to get a new beer type. Sometimes Lambic can consists of beer aged from five to six different years. Fruits and herbs are added into some beer types. Lambic differs in complex composition, but the beer can be always recognized by «branded» light sourness unique only to it. It is difficult to talk about Lambic, you have to try it.'}
        </p>
      </section>

      <section className={classes.section}>
        <div className={classes.title}>
          {activeLanguage === 1 ? 'Самые популярные сорта' : 'The most popular brands'}
        </div>

        <div className={classes.sortItem}>
          <div className={classes.subtitle}>{activeLanguage === 1 ? 'Чистый Ламбик' : 'Pure Lambic'}</div>
          <p>
            {activeLanguage === 1
              ? 'Неферментированный негазированный напиток, молодое пиво, которое выдерживается не более года и в которое не добавляются другие сорта. Имеет сухой, слегка кисловатый вкус.'
              : 'A non-fermented non-carbonated drink, young beer aged for not more than a year, and which is not mixed up with other beer types. It has a dry slightly sour taste.'}
          </p>
        </div>

        <div className={classes.sortItem}>
          <div className={classes.subtitle}>{activeLanguage === 1 ? 'Gueuze (Гёз)' : 'Gueuze'}</div>
          <p>
            {activeLanguage === 1
              ? 'Смесь однолетнего и более выдержанного Ламбика. Как правило, его подвергают вторичному брожению, отсюда классический вкус, удачно балансирующий между молодым и старым пивом.'
              : 'A mixture of one-year and more aged Lambic. Usually, it undergoes secondary fermentation, therefore it has its classical taste very well balancing between young and aged beer.'}
          </p>
        </div>

        <div className={classes.collageRow}>
          <div className={classes.collageCol}>
            <div className={classes.collageItem}>
              <LazyLoadImage src={aboutBeerImg1} alt='' />
            </div>
          </div>
          <div className={classes.collageCol}>
            <div className={classes.collageItem}>
              <LazyLoadImage src={aboutBeerImg2} alt='' />
            </div>
          </div>
        </div>

        <div className={classes.sortItem}>
          <div className={classes.subtitle}>{activeLanguage === 1 ? 'Oude Gueuze (Оуде гёз)' : 'Oude Gueuze'}</div>
          <p>
            {activeLanguage === 1
              ? 'Старый Ламбик (1-10 лет выдержки при смешивании) с плотным, насыщенным вкусом выдержанного пива.'
              : 'Old Lambic (aged 1 to 10 years when mixing) with a thick and rich taste of aged beer.'}
          </p>
        </div>

        <div className={classes.sortItem}>
          <div className={classes.subtitle}>{activeLanguage === 1 ? 'Faro (Фаро)' : 'Faro'}</div>
          <p>
            {activeLanguage === 1
              ? 'Оригинальный купаж легких и выдержанных сортов с добавлением коричневого сахара, карамели и иногда специальных трав. Имеет кисло-сладкий вкус и едва уловимую хмелевую горчинку.'
              : ' an original blend of light and aged types with addition of brown sugar, caramel and sometimes special herbs. It has a sour and sweet taste and just noticeable hops bitterness.'}
          </p>
        </div>

        <div className={classes.sortItem}>
          <div className={classes.subtitle}>{activeLanguage === 1 ? 'Фруктовые Ламбики' : 'Fruity lambics'}</div>
          <p>
            {activeLanguage === 1
              ? 'Это напитки с добавлением любых фруктов и ягод. Часто используют персик (Lambic Peche), малину (Lambic Frambozen), а также черную смородину(Cassis Lambic), виноград (Muscat), клубнику и другие фрукты.'
              : 'Drinks with addition of any fruits and berries. Peaches (Lambic Peche), raspberries (Lambic Frambozen), black current (Cassis Lambic), grapes (Muscat), strawberries and other fruits are often used.'}
          </p>
        </div>

        <div className={`${classes.collageRow} ${classes.collageRowNowrap}`}>
          <div className={classes.collageCol}>
            <div className={classes.collageItem}>
              <LazyLoadImage src={aboutBeerImg3} alt='' />
            </div>
          </div>
          <div className={classes.collageCol}>
            <div className={classes.collageItem}>
              <LazyLoadImage src={aboutBeerImg4} alt='' />
            </div>
          </div>
          <div className={classes.collageCol}>
            <div className={classes.collageItem}>
              <LazyLoadImage src={aboutBeerImg5} alt='' />
            </div>
          </div>
        </div>

        <div className={classes.sortItem}>
          <div className={classes.subtitle}>{activeLanguage === 1 ? 'Kriek (Крик)' : 'Kriek'}</div>
          <p>
            {activeLanguage === 1
              ? 'Один из самых популярных видов Ламбика, который производится с добавлением подвяленной вишни. Это придает пиву глубокий, насыщенный цвет и бархатистый вкус с легким оттенком миндаля. Дозревает в бутылках около года.'
              : 'One of the most popular types of Lambic, which is made using dried cherry. It gives beer deep rich colour and a velvet taste with a light nuance of almond. It takes it about one year to age in bottles.'}
          </p>
        </div>
      </section>

      <section className={classes.section}>
        <div className={classes.title}>{activeLanguage === 1 ? 'Читайте также' : 'Also read'}</div>
        <p>
          <Link
            className={classes.link}
            to={
              activeLanguage === 1
                ? '/articles/belgiyskoe-pivo-v-stile-kriek'
                : '/en/articles/belgiyskoe-pivo-v-stile-kriek'
            }
          >
            {activeLanguage === 1 ? 'Бельгийское вишневое пиво «Kriek»' : 'Belgian cherry beer "Kriek"'}
          </Link>
        </p>
      </section>
    </div>
  );
}
